import { findCountertopSection } from './find-countertop-section';
import { getCountertopRun } from './get-countertop-run';
import { CountertopSeamGroup, getCountertopSeamGroups } from './get-countertop-seam-groups';
import { getCountertopSections } from './get-countertop-sections';
import { hasCountertopSurface } from './has-countertop-surface';
import { MAX_UNSEAMED_SECTION_LENGTH, SEAM_POSITION_TOLERANCE } from '../constants';

import type { Countertop } from '../../countertops/countertop.class';
import type { CountertopSection, SeamDemand } from '../types';

export type GetSeamDemandsParams = {
	countertop: Countertop;
	/** Defaults to the documented 88" cap. */
	maxSectionLength?: number;
	/** Breakpoint centres to evaluate against; defaults to the ones the countertop currently holds. */
	centers?: Array<number> | null;
};

const isSeamedAt = (centers: Array<number>, seamGroup: CountertopSeamGroup): boolean => {
	return centers.some((center) => center >= seamGroup.left - SEAM_POSITION_TOLERANCE && center <= seamGroup.right + SEAM_POSITION_TOLERANCE);
};

const graniteApplianceDemands = (params: { countertop: Countertop; sections: Array<CountertopSection>; centers: Array<number>; maxSectionLength: number }): Array<SeamDemand> => {
	const { countertop, sections, centers, maxSectionLength } = params;

	return getCountertopSeamGroups(countertop)
		.filter((seamGroup) => seamGroup.demandsGraniteSeam && !isSeamedAt(centers, seamGroup))
		.map((seamGroup) => {
			const section = findCountertopSection({ sections, center: seamGroup.center });
			if (!section) return null;

			return {
				reason: 'granite-appliance',
				section,
				maxSectionLength,
				anchorGroupId: seamGroup.group.id,
				anchorItemId: seamGroup.anchorItemId,
				anchorSku: seamGroup.anchorSku
			} as SeamDemand;
		})
		.filter((demand): demand is SeamDemand => demand !== null);
};

/**
 * Every unsatisfied seam requirement on a countertop run.
 *
 * Concrete needs a seam only where a section exceeds the cap (`guide-2026:208-212`). Granite needs
 * one at grills, C1SLV, farm sinks and refreshment centres at ANY size (`guide-2026:213-216`) and
 * is still subject to the same cap. Sinks other than farm sinks are exempt (`guide-2026:218`), as
 * is the single side burner (`guide-2026:217`).
 * @param params the countertop, the cap to apply, and the breakpoint centres to judge against
 * @returns the demands, appliance-anchored ones first
 */
export const getSeamDemands = (params: GetSeamDemandsParams): Array<SeamDemand> => {
	const { countertop, maxSectionLength = MAX_UNSEAMED_SECTION_LENGTH, centers = null } = params;

	if (!countertop || !hasCountertopSurface(countertop)) return [];

	const run = getCountertopRun(countertop);
	if (!run) return [];

	const breakpointCenters = centers ?? countertop.breakpoints?.children.map((breakpoint) => breakpoint.center) ?? [];
	const sections = getCountertopSections({ run, centers: breakpointCenters });
	const { isGranite = false } = countertop.material ?? {};

	const applianceDemands = isGranite
		? graniteApplianceDemands({ countertop, sections, centers: breakpointCenters, maxSectionLength })
		: [];

	const lengthDemands = sections
		.filter((section) => section.length > maxSectionLength + SEAM_POSITION_TOLERANCE)
		.map((section) => ({
			reason: 'section-length',
			section,
			maxSectionLength,
			anchorGroupId: null,
			anchorItemId: null,
			anchorSku: null
		} as SeamDemand));

	return [...applianceDemands, ...lengthDemands];
};
